import { useMemo } from 'react';
import { useQuery, useIsFetching } from '@tanstack/react-query';
import { ContractsListSortType, getContractsCSV } from '@/lib/coloc-api/admin';
import { showToast } from '@/services/toast-service';
import { useContractsListFiltersContext } from '../../../hooks/use-get-contracts-list-filter';
import { useDebounce } from '@/hooks/use-debounce';
import { useContractsSortingContext } from '../../../hooks/use-contracts-sorting-context';

const useGetContractsCSV = () => {
  const {
    searchQuery,
    moveInStartDate,
    moveInEndDate,
    moveOutStartDate,
    moveOutEndDate,
    residencyRegistrationStatus,
    propertyManagerId: selectedPropertyManagerId,
    status,
    roomNumber,
  } = useContractsListFiltersContext();

  const debouncedSearchQuery: string = useDebounce(searchQuery ?? '', 500);

  const formatDateRange = (startDate?: Date, endDate?: Date) => {
    if (!startDate || !endDate) return undefined;
    return `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`;
  };

  const moveInDateRange = formatDateRange(moveInStartDate, moveInEndDate);
  const moveOutDateRange = formatDateRange(moveOutStartDate, moveOutEndDate);

  const {
    sortDirection,
    selectedColumnToSort,
  } = useContractsSortingContext();

  const { refetch: fetch } = useQuery({
    queryKey: ['getContractsCSV'],
    enabled: false,
    queryFn: async () => {
      const response = await getContractsCSV({
        ...(moveInDateRange && { move_in: moveInDateRange }),
        ...(moveOutDateRange && { move_out: moveOutDateRange }),
        ...(debouncedSearchQuery && { query: debouncedSearchQuery }),
        ...(status && { status: status.slug }),
        ...(selectedPropertyManagerId && {
          property_manager_id: selectedPropertyManagerId,
        }),
        ...(residencyRegistrationStatus && {
          residency_registration_status: residencyRegistrationStatus.slug,
        }),
        ...(selectedColumnToSort && { sort: selectedColumnToSort }),
        ...(sortDirection && { order: sortDirection }),
        ...(roomNumber && { room_number: roomNumber }),
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      let filename = 'contracts.csv';
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition
          .split('filename=')[1]
          .split(';')[0]
          .replace(/"/g, '');
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return response;
    },
    onSuccess: () => {
      showToast.success({
        title: 'Contract CSV Export!',
        text: 'CSV exported successfully',
      });
    },
    onError: () => {
      showToast.error({
        title: 'Contract CSV Export!',
        text: 'Failed to export CSV',
      });
    },
  });

  const numOfContractCSVFetches = useIsFetching({
    queryKey: ['getContractsCSV'],
  });

  const isExportingContracts = useMemo(() => {
    return numOfContractCSVFetches >= 1;
  }, [numOfContractCSVFetches]);

  return {
    fetch,
    isExportingContracts,
  };
};

export { useGetContractsCSV };
